import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { lazy } from 'react';
import AuthGuard from '@/components/auth/AuthGuard';
import FeatureFlagGuard from '@/components/FeatureFlagGuard';
import DashboardLayout from '@/layouts/DashboardLayout';
import RevisionLayout from '@/layouts/RevisionLayout';
import RouterWrapper from '@/providers/RouterWrapper';
import { SuspenseWrapper } from '@/components/SuspenseWrapper';
import { RouteErrorElement } from '@/components/RouteErrorElement';
import { FeatureFlag } from '@lingugeek/ui';

// Lazy load all pages
const LoginPage = lazy(() => import('@/pages/login'));
const DashboardHome = lazy(() => import('@/pages/dashboard/main'));
const AccountPage = lazy(() => import('@/pages/dashboard/account'));
const PreferencesPage = lazy(() => import('@/pages/dashboard/preferences'));
const WordsPage = lazy(() => import('@/pages/dashboard/words'));
const ProgressPage = lazy(() => import('@/pages/dashboard/progress'));
const SchedulePage = lazy(() => import('@/pages/dashboard/schedule'));
const GoalsPage = lazy(() => import('@/pages/dashboard/goals'));
const RevisionPage = lazy(() => import('@/pages/dashboard/revision'));
const RevisionCollectionPage = lazy(
  () => import('@/pages/dashboard/revision/collection')
);
const RevisionStudyPage = lazy(
  () => import('@/pages/dashboard/revision/study')
);
const CheckoutPage = lazy(() => import('@/pages/dashboard/checkout'));

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RouterWrapper />,
    errorElement: <RouteErrorElement />,
    children: [
      {
        path: 'login',
        element: (
          <SuspenseWrapper>
            <LoginPage />
          </SuspenseWrapper>
        ),
        errorElement: <RouteErrorElement />,
      },
      {
        element: (
          <AuthGuard>
            <Outlet />
          </AuthGuard>
        ),
        children: [
          {
            element: (
              <DashboardLayout>
                <Outlet />
              </DashboardLayout>
            ),
            children: [
              {
                index: true,
                element: (
                  <SuspenseWrapper>
                    <DashboardHome />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'account',
                element: (
                  <SuspenseWrapper>
                    <AccountPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'preferences',
                element: (
                  <SuspenseWrapper>
                    <PreferencesPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'words',
                element: (
                  <SuspenseWrapper>
                    <WordsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'progress',
                element: (
                  <SuspenseWrapper>
                    <ProgressPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'schedule',
                element: (
                  <SuspenseWrapper>
                    <SchedulePage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'goals',
                element: (
                  <SuspenseWrapper>
                    <GoalsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'checkout',
                element: (
                  <FeatureFlagGuard flag={FeatureFlag.ENABLE_SUBSCRIPTIONS}>
                    <SuspenseWrapper>
                      <CheckoutPage />
                    </SuspenseWrapper>
                  </FeatureFlagGuard>
                ),
              },
              {
                path: 'revision',
                element: <RevisionLayout />,
                children: [
                  {
                    index: true,
                    element: (
                      <SuspenseWrapper>
                        <RevisionPage />
                      </SuspenseWrapper>
                    ),
                  },
                  {
                    path: ':collectionId',
                    element: (
                      <SuspenseWrapper>
                        <RevisionCollectionPage />
                      </SuspenseWrapper>
                    ),
                  },
                  {
                    path: ':collectionId/study',
                    element: (
                      <SuspenseWrapper>
                        <RevisionStudyPage />
                      </SuspenseWrapper>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to='/' replace />,
  },
]);
